// Implements: F-UIX-018, F-OBS-005, F-OBS-011, F-UIX-010
/**
 * "Auditoría" (UIX.7): the audit trail of an organization the person administers, filtered by
 * range, action, team and mockup, a page at a time.
 *
 * `AuditByOrganization` offers the organizations whose scope the server answers with
 * `audit:read_organization` and mounts one `AuditContainer` keyed by the chosen organization's id
 * (checklist 14): switching organization mounts a fresh one, so no filter, page or late answer of
 * the previous organization reaches the next one's screen (the reads are keyed by its id too).
 *
 * Mockups are named as the trail names them (trashed and never-generated ones included, the
 * trashed marked), else by the per-mockup report; the mockup filter offers every mockup either
 * named, and keeps offering one the trail named under an earlier filter (7.S13.12).
 *
 * The filters are a draft until "Aplicar"; a reversed or too long range is refused on the spot,
 * without asking the server. The server decides every read; a refusal is shown as an error state
 * with its retry, except a "Cargar más" whose cursor the server refuses as another list's: the list
 * starts again from its first page, with a line saying so (7.S13.13). The list has its own loading, error and empty state (F-UIX-010). No storage.
 */

import { useId, useMemo, useState } from "react";
import type { ReactElement } from "react";

import { asApiError } from "../../../shared/api/asApiError";
import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { EmptyState } from "../../../shared/ui/molecules/EmptyState";
import { ErrorState } from "../../../shared/ui/molecules/ErrorState";
import { LoadingState } from "../../../shared/ui/molecules/LoadingState";
import type { BackofficeScope } from "../../../types/api";
import { AuditFiltersForm } from "../components/AuditFiltersForm";
import type { FilterOption } from "../components/AuditFiltersForm";
import { AuditTable } from "../components/AuditTable";
import sectionStyles from "../components/MyUsage.module.css";
import { useAudit } from "../hooks/useAudit";
import { NO_FILTERS, auditTargetsOf, namedMockupsOf, rangeProblem, withSeenMockups } from "../model/audit";
import type { NamedMockup } from "../model/audit";
import type { AuditFilters, AuditTarget } from "../../../types/audit";
import { calendarDayText } from "../model/myUsage";

type Translate = ReturnType<typeof useTranslation>["t"];

function targetLabel(target: AuditTarget, t: Translate): string {
  return target.name === null
    ? t("backoffice.consumption.target.unnamedOrganization")
    : t("backoffice.consumption.target.organization", { name: target.name });
}

export interface AuditContainerProps {
  readonly target: AuditTarget;
}

/** One organization's trail. Mount it keyed by `target.organizationId`. */
export function AuditContainer({ target }: AuditContainerProps): ReactElement {
  const { t } = useTranslation();
  const headingId = useId();
  const listId = useId();
  const [draft, setDraft] = useState<AuditFilters>(NO_FILTERS);
  const [applied, setApplied] = useState<AuditFilters>(NO_FILTERS);
  const [problem, setProblem] = useState<string | null>(null);
  const { trail, teams, mockups, loadMore } = useAudit(target, applied);
  const [restarted, setRestarted] = useState(false);
  const [seenMockups, setSeenMockups] = useState<ReadonlyMap<string, NamedMockup>>(() => new Map());

  const pages = trail.data?.pages ?? [];
  const rows = pages.flatMap((page) => page.items);
  const range = pages[0]?.range ?? null;
  // The mockups the trail named so far, kept across filters; updated only when a row names a new one.
  const seenNow = withSeenMockups(seenMockups, rows);
  if (seenNow !== seenMockups) setSeenMockups(seenNow);

  const teamOptions = useMemo<FilterOption[]>(
    () => (teams.data?.teams ?? []).map((team) => ({ id: team.id, name: team.name })),
    [teams.data],
  );
  const namedMockups = useMemo(() => namedMockupsOf(mockups.data?.rows ?? [], seenNow), [mockups.data, seenNow]);
  const mockupOptions = useMemo<FilterOption[]>(
    () =>
      namedMockups.map((mockup) => ({
        id: mockup.id,
        name: mockup.trashed ? `${mockup.name} ${t("backoffice.audit.mockup.trashed")}` : mockup.name,
      })),
    [namedMockups, t],
  );
  const teamNames = useMemo(() => new Map(teamOptions.map((team) => [team.id, team.name] as const)), [teamOptions]);
  const mockupNames = useMemo(
    () => new Map(namedMockups.map((mockup) => [mockup.id, mockup] as const)),
    [namedMockups],
  );

  function body(): ReactElement {
    if (trail.isError && pages.length === 0) {
      return (
        <ErrorState
          headingLevel={3}
          error={asApiError(trail.error)}
          onRetry={() => {
            void trail.refetch();
          }}
        />
      );
    }
    if (trail.data === undefined) return <LoadingState label={t("backoffice.audit.loading")} />;
    if (rows.length === 0) {
      return (
        <EmptyState headingLevel={3} title={t("backoffice.audit.empty.title")} body={t("backoffice.audit.empty.body")} />
      );
    }
    return (
      <>
        <AuditTable labelledBy={listId} rows={rows} teamNames={teamNames} mockupNames={mockupNames} />
        {trail.hasNextPage ? (
          <Button
            variant="secondary"
            type="button"
            loading={trail.isFetchingNextPage}
            disabled={trail.isFetchingNextPage}
            onClick={() => {
              void loadMore().then(setRestarted);
            }}
          >
            {t("backoffice.audit.more")}
          </Button>
        ) : null}
        {trail.isError ? (
          <ErrorState
            headingLevel={3}
            error={asApiError(trail.error)}
            onRetry={() => {
              void trail.fetchNextPage();
            }}
          />
        ) : null}
      </>
    );
  }

  return (
    <section className={sectionStyles.section} aria-labelledby={headingId}>
      <h2 id={headingId} className={sectionStyles.sectionTitle}>
        {targetLabel(target, t)}
      </h2>
      <AuditFiltersForm
        draft={draft}
        teams={teamOptions}
        mockups={mockupOptions}
        problem={problem}
        onChange={setDraft}
        onApply={() => {
          const wrong = rangeProblem(draft.from, draft.to);
          setProblem(wrong === null ? null : t(`backoffice.audit.problem.${wrong}`));
          if (wrong === null) {
            setApplied(draft);
            setRestarted(false);
          }
        }}
        onClear={() => {
          setProblem(null);
          setRestarted(false);
          setDraft(NO_FILTERS);
          setApplied(NO_FILTERS);
        }}
      />
      <h3 id={listId} className={sectionStyles.sectionTitle}>
        {t("backoffice.audit.list.title")}
      </h3>
      {range === null ? null : (
        <p className={sectionStyles.hint}>
          {t("backoffice.myUsage.period.range", { from: calendarDayText(range.from), to: calendarDayText(range.to) })}
        </p>
      )}
      {restarted ? (
        <p className={sectionStyles.hint} role="status">
          {t("backoffice.audit.restarted")}
        </p>
      ) : null}
      {body()}
    </section>
  );
}

export interface AuditByOrganizationProps {
  /** The session's scopes: the organizations the person administers. */
  readonly scopes: readonly BackofficeScope[];
}

export function AuditByOrganization({ scopes }: AuditByOrganizationProps): ReactElement {
  const { t } = useTranslation();
  const pickerId = useId();
  const targets = useMemo(() => auditTargetsOf(scopes), [scopes]);
  const [chosen, setChosen] = useState<string | null>(null);
  const target = targets.find((candidate) => candidate.organizationId === chosen) ?? targets[0];

  if (target === undefined) {
    return <EmptyState title={t("backoffice.audit.none.title")} body={t("backoffice.audit.none.body")} />;
  }

  return (
    <div className={sectionStyles.section}>
      {targets.length > 1 ? (
        <p>
          <label htmlFor={pickerId}>{t("backoffice.audit.target.label")}</label>{" "}
          <select
            id={pickerId}
            value={target.organizationId}
            onChange={(event) => {
              setChosen(event.target.value);
            }}
          >
            {targets.map((candidate) => (
              <option key={candidate.organizationId} value={candidate.organizationId}>
                {targetLabel(candidate, t)}
              </option>
            ))}
          </select>
        </p>
      ) : null}
      <AuditContainer key={target.organizationId} target={target} />
    </div>
  );
}
